/**
 * Parsing Options Panel Component (issue #57)
 *
 * Collapsible sidebar section exposing the docling-serve parse options:
 * formula recognition, OCR mode, table structure/mode, and page range.
 * Relocated here from the account-settings modal's "Parsing" tab — only
 * the LOCATION of the controls changed; the store slice
 * (`useAppStore().parseSettings` / `setParseSettings`) and the mapper
 * (`parse-options-mapper.ts`) are unchanged.
 */

import { useAppStore } from '../stores/app-store';
import { BaseComponent, type BaseComponentOptions } from './base-component';

export type ParsingOptionsPanelOptions = BaseComponentOptions;

class ParsingOptionsPanel extends BaseComponent {
  protected getDefaultCssClass(): string {
    return 'parsing-options-panel';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return {
      'parsingOptions.formula': 'Recognize formulas (LaTeX)',
      'parsingOptions.ocr': 'OCR',
      'parsingOptions.ocrAuto': 'Auto',
      'parsingOptions.ocrOff': 'Off',
      'parsingOptions.ocrForce': 'Force',
      'parsingOptions.tableStructure': 'Parse tables',
      'parsingOptions.tableMode': 'Table accuracy',
      'parsingOptions.tableAccurate': 'Accurate',
      'parsingOptions.tableFast': 'Fast',
      'parsingOptions.pages': 'Pages (leave blank for all)',
      'parsingOptions.pageRangeError':
        'Page range must be whole numbers with start ≥ 1 and end ≥ start.',
    };
  }

  /**
   * Render the four control groups into the container. Reflects the
   * current `useAppStore().parseSettings` on every render (initial mount
   * and language-change re-renders).
   */
  protected override render(): void {
    if (!this.container) return;

    const s = useAppStore.getState().parseSettings;
    const cls = this.baseCssClass;
    const from = s.pageRange ? String(s.pageRange.from) : '';
    const to = s.pageRange ? String(s.pageRange.to) : '';
    const sel = (a: string, b: string) => (a === b ? 'selected' : '');

    this.container.innerHTML = `
      <div class="${cls}__field mb-2">
        <label class="${cls}__label form-check d-flex align-items-center gap-1 mb-0">
          <input type="checkbox" class="form-check-input mt-0" data-parse="formula" ${s.formulaRecognition ? 'checked' : ''} />
          <span>${this.t('parsingOptions.formula')}</span>
        </label>
      </div>
      <div class="${cls}__field mb-2">
        <label class="${cls}__label form-label small mb-1">${this.t('parsingOptions.ocr')}</label>
        <select class="${cls}__input form-select form-select-sm" data-parse="ocr-mode">
          <option value="auto" ${sel('auto', s.ocr.mode)}>${this.t('parsingOptions.ocrAuto')}</option>
          <option value="off" ${sel('off', s.ocr.mode)}>${this.t('parsingOptions.ocrOff')}</option>
          <option value="force" ${sel('force', s.ocr.mode)}>${this.t('parsingOptions.ocrForce')}</option>
        </select>
      </div>
      <div class="${cls}__field mb-2">
        <label class="${cls}__label form-check d-flex align-items-center gap-1 mb-0">
          <input type="checkbox" class="form-check-input mt-0" data-parse="table-structure" ${s.table.structure ? 'checked' : ''} />
          <span>${this.t('parsingOptions.tableStructure')}</span>
        </label>
      </div>
      <div class="${cls}__field mb-2">
        <label class="${cls}__label form-label small mb-1">${this.t('parsingOptions.tableMode')}</label>
        <select class="${cls}__input form-select form-select-sm" data-parse="table-mode">
          <option value="accurate" ${sel('accurate', s.table.mode)}>${this.t('parsingOptions.tableAccurate')}</option>
          <option value="fast" ${sel('fast', s.table.mode)}>${this.t('parsingOptions.tableFast')}</option>
        </select>
      </div>
      <div class="${cls}__field mb-2">
        <label class="${cls}__label form-label small mb-1">${this.t('parsingOptions.pages')}</label>
        <div class="d-flex gap-2">
          <input type="number" min="1" class="${cls}__input form-control form-control-sm" data-parse="page-from" value="${from}" />
          <input type="number" min="1" class="${cls}__input form-control form-control-sm" data-parse="page-to" value="${to}" />
        </div>
      </div>
      <div class="${cls}__message small" data-message="parsing"></div>
    `;
  }

  protected override onInitialize(): void {
    if (!this.container) return;
    this.container.addEventListener('change', () => this.handleParsingChange());
  }

  /**
   * Show/clear the inline validation message.
   */
  private showMessage(
    text: string,
    type: 'success' | 'error' = 'success'
  ): void {
    const messageEl = this.container?.querySelector('[data-message="parsing"]');
    if (messageEl) {
      messageEl.textContent = text;
      messageEl.className = `${this.baseCssClass}__message ${this.baseCssClass}__message--${type}`;
    }
  }

  /**
   * Handle a change on any control: validate the page range, write to the
   * store via `setParseSettings`, and show/clear the inline error message.
   */
  private handleParsingChange(): void {
    const q = (sel: string) =>
      this.container?.querySelector(sel) as
        HTMLInputElement | HTMLSelectElement | null;

    const formula =
      (q('[data-parse="formula"]') as HTMLInputElement)?.checked ?? true;
    const ocrMode = (q('[data-parse="ocr-mode"]') as HTMLSelectElement)
      ?.value as 'auto' | 'off' | 'force';
    const tableStructure =
      (q('[data-parse="table-structure"]') as HTMLInputElement)?.checked ??
      true;
    const tableMode = (q('[data-parse="table-mode"]') as HTMLSelectElement)
      ?.value as 'accurate' | 'fast';

    const fromRaw = (
      q('[data-parse="page-from"]') as HTMLInputElement
    )?.value.trim();
    const toRaw = (
      q('[data-parse="page-to"]') as HTMLInputElement
    )?.value.trim();

    let pageRange: { from: number; to: number } | null = null;
    if (fromRaw || toRaw) {
      const from = Number(fromRaw);
      const to = Number(toRaw);
      if (
        !Number.isInteger(from) ||
        !Number.isInteger(to) ||
        from < 1 ||
        to < from
      ) {
        this.showMessage(this.t('parsingOptions.pageRangeError'), 'error');
        // Persist everything except the invalid page range.
        useAppStore.getState().setParseSettings({
          formulaRecognition: formula,
          ocr: {
            mode: ocrMode,
            languages: useAppStore.getState().parseSettings.ocr.languages,
          },
          table: { structure: tableStructure, mode: tableMode },
          pageRange: null,
        });
        return;
      }
      pageRange = { from, to };
    }

    this.showMessage('');
    useAppStore.getState().setParseSettings({
      formulaRecognition: formula,
      ocr: {
        mode: ocrMode,
        languages: useAppStore.getState().parseSettings.ocr.languages,
      },
      table: { structure: tableStructure, mode: tableMode },
      pageRange,
    });
  }
}

export default ParsingOptionsPanel;
